import { record } from '@glimt/record';
import { WebWorker } from '../../managers/webWorker';
import { StringObfuscationRegexes } from '../../obfuscateRules';
import { OBFUSCATION_SELECTOR_STORAGE_KEY } from '../../managers/storageConstants';
import { StorageManager } from '../../managers/storageManager';
import type { RecordOptions } from '../../types';
import type { AdapterCallbacks, RecordAdapter } from './adapterTypes';
import {
  EventSourceType,
  type RecordEvent,
  type UnProcessedRecordEvent,
} from './types';
import { Recapt } from '../../engine';
const LAZY_POLL_THRESHOLD = 20;
const CSS_RESOLUTION_TIME_IN_MEMORY = 2500;

const ActiveEventSources = [
  EventSourceType.MouseMove,
  EventSourceType.MouseInteraction,
  EventSourceType.Scroll,
  EventSourceType.ViewportResize,
  EventSourceType.Input,
  EventSourceType.TouchMove,
  EventSourceType.MediaInteraction,
  // EventSourceType.StyleSheetRule, keeping for now
  EventSourceType.CanvasMutation,
  EventSourceType.Drag,
];

interface CssResolutionEntry {
  waitStarted: number;
  requestIds: string[];
  event: UnProcessedRecordEvent;
  resolutions: Record<string, string>;
}

export class RrwebAdapter implements RecordAdapter {
  private callbacks!: AdapterCallbacks;
  private isRecording = false;

  getEngineName(): string {
    return 'rrweb';
  }
  private stopFn?: VoidFunction;
  private worker?: WebWorker;
  private keyMappings: Record<number, string> = {};
  private keyMappingsToSync: number[] = [];
  private lazyPollCount = 0;
  private cssResolutionStore: { entries: CssResolutionEntry[] } = {
    entries: [],
  };
  private rrRecordSettings: { maskTextSelector?: string } = {};
  private zendeskStatus = {
    polls: 0,
    maxPolls: 100,
    loaded: false,
    failed: false,
  };
  private customEventHandler: ((event: Event) => void) | null = null;

  init(callbacks: AdapterCallbacks): void {
    this.callbacks = callbacks;
    this.setupWorker();
    this.checkObfuscationSelector();
    window._rrweb_is_debug = !!Recapt.DEBUG;
    this.setupCustomEventListener();
  }

  startRecording(): void {
    this.doRecord();
  }

  stopRecording(): void {
    this.stopFn?.();
    this.isRecording = false;
  }

  cleanup(): void {
    this.stopRecording();
    this.worker?.stop();
    this.removeCustomEventListener();
  }

  onNewSession(): void {
    if (!this.isRecording) return;
    Recapt.debug('onNewSession, taking full snapshot');
    record.takeFullSnapshot();
  }

  getRequestNamespace(): string {
    return 'record';
  }

  takeFullSnapshot(): void {
    record.takeFullSnapshot();
  }

  setOptions(opts: RecordOptions): void {
    Recapt.debug('setting record options', opts);
    if (opts.disableRefetchesCausedByCors === true) {
      window._rrweb_skip_re_fetching_to_suppress_cors_errors = true;
      Recapt.debug(
        'did set window._rrweb_skip_re_fetching_to_suppress_cors_errors, value:',
        window._rrweb_skip_re_fetching_to_suppress_cors_errors,
      );
    }
  }

  purgeState(): void {
    this.keyMappings = {};
    this.keyMappingsToSync = [];
    this.cssResolutionStore = { entries: [] };
    this.worker?.emit('purge-state', '');
  }

  buildRequestExtras(): Record<string, unknown> {
    const keyMappings: Record<number, string> = {};
    for (const keyToSync of this.keyMappingsToSync) {
      keyMappings[keyToSync] = this.keyMappings[keyToSync];
    }
    this.keyMappingsToSync = [];
    return { keyMappings };
  }

  getStorableState(): Record<string, unknown> {
    return {
      keyMappings: this.keyMappings,
      keyMappingsToSync: this.keyMappingsToSync,
      cssResolutionStore: this.cssResolutionStore,
    };
  }

  restoreStoredState(stored: any): void {
    if (!stored) return;
    this.keyMappings = stored.keyMappings ?? {};
    this.keyMappingsToSync = stored.keyMappingsToSync ?? [];
    this.cssResolutionStore = stored.cssResolutionStore ?? { entries: [] };
  }

  private setupWorker(): void {
    this.worker = new WebWorker('recordWorker');
    this.worker.start();
    this.worker.emit('regexes:load', StringObfuscationRegexes);

    if (Recapt.DEBUG) this.worker.emit('debug-mode', {});

    this.worker.on<RecordEvent>('event:parsed', (payload) => {
      this.callbacks.pushEvent(payload);
    });

    this.worker.on<Record<number, string>>('keymapping:added', (payload) => {
      this.keyMappingsToSync.push(
        ...(Object.keys(payload) as unknown as number[]),
      );
      this.keyMappings = { ...this.keyMappings, ...payload };
    });

    this.worker.on<UnProcessedRecordEvent>(
      'event:awaiting-resolutions',
      (payload) => {
        if (!payload.requestedResolutions) return;
        this.cssResolutionStore.entries.push({
          waitStarted: Date.now(),
          requestIds: payload.requestedResolutions,
          event: { ...payload, requestedResolutions: undefined },
          resolutions: {},
        });
      },
    );
  }

  private checkObfuscationSelector(): void {
    const obfuscationSelector = StorageManager.getItem(
      OBFUSCATION_SELECTOR_STORAGE_KEY,
    );
    if (obfuscationSelector) {
      this.updateRecordSettings({ maskTextSelector: obfuscationSelector });
    }
  }

  updateRecordSettings(settings: typeof this.rrRecordSettings): void {
    this.rrRecordSettings = settings;
    this.callbacks.purgeAllState();
    this.purgeState();

    if (this.isRecording) {
      this.stopRecording();
      this.doRecord();
    }
  }

  private doRecord(): void {
    if (typeof window === 'undefined') return;

    if (
      Recapt.hasSupportSystem('zendesk') &&
      !this.zendeskStatus.failed &&
      !this.zendeskStatus.loaded
    ) {
      Recapt.debug('detected zendesk, awaiting load');
      this.waitForZendesk().then(() => {
        if (this.zendeskStatus.loaded) {
          Recapt.debug('detected zendesk loaded, starting recording');
          this.doRecord();
        } else {
          Recapt.debug('detected zendesk not loaded, shutting down...');
          Recapt.destroy();
        }
      });
      return;
    }

    if (this.isRecording) return;
    this.isRecording = true;

    Recapt.debug('starting rrweb recording');
    const stopFn = record({
      emit: (event) => {
        this.handleEvent({
          event,
          timestamp: Date.now(),
          where: Recapt.where(),
        });
      },
      recordCanvas: true,
      recordCrossOriginIframes: true,
      collectFonts: true,
      inlineStylesheet: true,
      inlineImages: true,
      maskAllInputs: true,
      userTriggeredOnInput: true,
      recordDOM: true,
      checkoutEveryNms: 20000,
      checkoutEveryNth: 250,
      maskTextSelector: this.rrRecordSettings.maskTextSelector,
    });

    if (!stopFn) {
      Recapt.onFatalError();
    } else {
      this.stopFn = stopFn;
    }
  }

  private handleEvent(event: UnProcessedRecordEvent): void {
    this.lazyPollCount++;
    if (this.lazyPollCount > LAZY_POLL_THRESHOLD) {
      this.cleanCssResolutionStore();
      this.lazyPollCount = 0;
    }

    const processedEvent = this.processEvent(event);
    if (!processedEvent) {
      Recapt.err('failed to process event', event);
      return;
    }

    if (
      'data' in processedEvent.event &&
      'source' in (processedEvent.event as any).data
    ) {
      const source = (processedEvent.event as any).data
        .source as EventSourceType;
      this.callbacks.pushLatestEventType(source, processedEvent.timestamp);
    }

    this.offloadEvent(processedEvent, true);
  }

  private processEvent(event: UnProcessedRecordEvent): UnProcessedRecordEvent {
    if (
      ActiveEventSources.includes(
        event.event.type as unknown as EventSourceType,
      )
    ) {
      this.callbacks.handleInactiveTime(event.timestamp);
    }

    event.event.timestamp = this.callbacks.getRenderTimestamp(
      event.event.timestamp,
    );
    event.timestamp = this.callbacks.getRenderTimestamp(event.timestamp);

    return event;
  }

  private offloadEvent(event: UnProcessedRecordEvent, canReject = false): void {
    this.worker?.emit('event', { ...event, canReject: !!canReject });
  }

  private async waitForZendesk(): Promise<void> {
    await new Promise<void>((resolve) => {
      const check = () => {
        this.zendeskStatus.polls++;

        if (this.zendeskStatus.polls >= this.zendeskStatus.maxPolls) {
          this.zendeskStatus.failed = true;
          return resolve();
        }

        if (
          'zE' in window &&
          window.zE &&
          Boolean(
            document.querySelector(
              'iframe[title="Button to launch messaging window, conversation in progress"]',
            ) ??
            document.querySelector(
              'iframe[title="Button to launch messaging window"]',
            ) ??
            document.querySelector('iframe[src*="zendesk.com"]'),
          )
        ) {
          this.zendeskStatus.loaded = true;
          resolve();
        } else {
          setTimeout(check, 250);
        }
      };
      check();
    });
  }

  private cleanCssResolutionStore(): void {
    const now = Date.now();
    for (let i = this.cssResolutionStore.entries.length - 1; i >= 0; i--) {
      const entry = this.cssResolutionStore.entries[i];
      if (now - entry.waitStarted > CSS_RESOLUTION_TIME_IN_MEMORY) {
        this.applyCssResolutionsToEntry(entry);
        this.offloadEvent(entry.event);
        this.cssResolutionStore.entries.splice(i, 1);
      }
    }
  }

  private applyCssResolutions(
    node: any,
    resolutions: Record<string, string>,
  ): any {
    if (
      node.attributes &&
      node.attributes._requestCssId &&
      !node.attributes._cssText
    ) {
      const cssText = resolutions[node.attributes._requestCssId];
      if (cssText) {
        node.attributes._cssText = cssText;
        delete node.attributes._requestCssId;
      }
    }

    if (node.childNodes) {
      node.childNodes = (node.childNodes as any[]).map((child) =>
        this.applyCssResolutions(child, resolutions),
      );
    }

    return node;
  }

  private applyCssResolutionsToEntry(entry: CssResolutionEntry): void {
    try {
      if (
        !('node' in (entry.event.event.data as any)) ||
        !entry.resolutions ||
        Object.keys(entry.resolutions).length === 0
      )
        return;

      (entry.event.event.data as any).node = this.applyCssResolutions(
        (entry.event.event.data as any).node,
        entry.resolutions,
      );
    } catch (err) {
      Recapt.err('catched css resolution error', err);
      Recapt.debug('error entry', entry);
    }
  }

  private handleCssResolution(requestCssId: string, cssText: string): void {
    for (let i = this.cssResolutionStore.entries.length - 1; i >= 0; i--) {
      const entry = this.cssResolutionStore.entries[i];

      if (!entry.requestIds.includes(requestCssId)) continue;

      entry.resolutions[requestCssId] = cssText;

      if (Object.keys(entry.resolutions).length === entry.requestIds.length) {
        this.applyCssResolutionsToEntry(entry);
        this.offloadEvent(entry.event);
        this.cssResolutionStore.entries.splice(i, 1);
        continue;
      }
    }

    this.cleanCssResolutionStore();
  }

  private setupCustomEventListener(): void {
    this.customEventHandler = (event: Event) => {
      const data = (event as CustomEvent).detail;
      if (!('data' in data)) return;
      if (!('tag' in data.data)) return;
      if (data.data.tag !== 'async-css-resolution') return;

      const requestCssId = data.data.requestCssId;
      const cssText = data.data.cssText;
      if (!requestCssId || !cssText) return;

      this.handleCssResolution(requestCssId, cssText);
    };

    window.addEventListener(
      '__rrweb_custom_event__',
      this.customEventHandler as EventListenerOrEventListenerObject,
    );
  }

  private removeCustomEventListener(): void {
    if (this.customEventHandler) {
      window.removeEventListener(
        '__rrweb_custom_event__',
        this.customEventHandler as EventListenerOrEventListenerObject,
      );
      this.customEventHandler = null;
    }
  }
}
